import type { JSX } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { ActionIcon, Alert, Badge, Button, Center, Group, Loader, Menu, Select, Stack, Tabs, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ApiError, offersApi, type Constellation, type Offer, type OfferVariant, type OfferVariantData } from '../../api';
import { usePageDownloads } from '../../export/downloads';
import { downloadXlsx } from '../../export/xlsx';
import { CONSTELLATION_LABELS, OFFER_STATUS, OFFER_TABS, todayIso } from '../../format';
import { linkTo } from '../../ui/links';
import { OfferCalculation } from './OfferCalculation';
import { OfferCompare } from './OfferCompare';
import { OfferDetails } from './OfferDetails';
import { OfferFiles } from './OfferFiles';
import { OfferAi } from './OfferAi';
import { offerSheets } from './offerSheets';
import { useOfferActions } from './OfferActions';
import { useCan } from '../../core/useMe';
import { useContextMenu } from '../../ui/ContextMenu';
import { OfferHoai } from './OfferHoai';
import { ProjectFromOfferModal } from './ProjectFromOffer';

const TABS = OFFER_TABS;

/** One offer: the AI evaluation of its documents, its details and texts, the calculation per variant, the comparison, the files. */
export function OfferPage(): JSX.Element {
  const { offerId = '', tab } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const offer = useQuery({ queryKey: ['offer', offerId], queryFn: () => offersApi.get(offerId) });
  const canWrite = useCan('offers.write');
  // Bumped after a restore: the tabs keep their own copy of the data while
  // editing, so they start afresh from the restored offer.
  const restored = useQuery({ queryKey: ['offer-restored', offerId], queryFn: () => 0, staleTime: Infinity }).data ?? 0;
  const actions = useOfferActions(() => navigate('/angebote'));
  const context = useContextMenu<Offer>((o, close) => actions.items(o, close));
  // Controlled so an entry in a submenu closes the ⋯ menu too.
  const [menuOpen, setMenuOpen] = useState(false);
  // A snapshot on opening, when something changed since the last one (ADR 0018).
  useEffect(() => {
    if (canWrite && offerId) void offersApi.takeSnapshot(offerId, 'open').catch(() => undefined);
  }, [offerId, canWrite]);
  const current = TABS.find((t) => t.path === tab);
  // Files of the offer, in the one download menu like every page (AP-100).
  usePageDownloads('offer', offer.data ? [
    { key: 'offer-xlsx', label: 'Angebot als Excel', hint: 'Angaben und Kalkulation je Variante', onClick: () => void offersApi.get(offerId).then((o) => downloadXlsx(offerSheets(o), `Angebot ${o.code} ${todayIso()}.xlsx`)) },
    { key: 'offer-json', label: 'Angebot als Datei (JSON)', hint: 'zum Weitergeben und Einlesen in PST; ohne Bankdaten, Steuernummer, Geburtsdatum', href: offersApi.exportUrl(offerId) },
  ] : []);
  if (offer.isPending) return <Center h={200}><Loader /></Center>;
  if (offer.isError) return <Alert color="red">{offer.error instanceof ApiError ? offer.error.message : 'Das Angebot konnte nicht geladen werden.'}</Alert>;
  if (!current) return <Navigate to={`/angebote/${offerId}/kalkulation`} replace />;
  const o = offer.data;
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['offer', offerId] });
  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      <Stack gap="sm" pb="xl">
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Group gap="xs" wrap="nowrap" onContextMenu={(e) => context.open(e, o)}>
            <ActionIcon component="a" variant="subtle" color="gray" size="sm" {...linkTo(navigate, '/angebote')} aria-label="Zur Angebotsliste" title="Zur Angebotsliste">{'←'}</ActionIcon>
            <Text ff="monospace" c="dimmed" size="sm">{o.code}</Text>
            <Text fw={700} size="lg">{o.name}</Text>
            <StatusMenu offer={o} onDone={refresh} />
            <ProjectButton offer={o} />
            <Menu position="bottom-start" withinPortal width={240} opened={menuOpen} onChange={setMenuOpen}>
              <Menu.Target><ActionIcon variant="subtle" color="gray" size="sm" aria-label={`Aktionen für ${o.code}`} title="Snapshots, Herunterladen, Duplizieren, Löschen">{'⋯'}</ActionIcon></Menu.Target>
              <Menu.Dropdown>{actions.items(o, () => setMenuOpen(false))}</Menu.Dropdown>
            </Menu>
          </Group>
          <Tabs value={current.path} variant="pills">
            <Tabs.List>
              {TABS.map((t) => <Tabs.Tab key={t.path} value={t.path} component="a" {...linkTo(navigate, `/angebote/${offerId}/${t.path}`)}>{t.label}</Tabs.Tab>)}
            </Tabs.List>
          </Tabs>
        </Group>
        {actions.dialogs}
        {context.menu}
        <Fragment key={restored}>
        {current.path === 'auswertung' && <OfferAi offer={o} onChanged={refresh} />}
        {current.path === 'angaben' && <OfferDetails offer={o} onSaved={refresh} />}
        {current.path === 'kalkulation' && <VariantsView offer={o} onChanged={refresh} />}
        {current.path === 'vergleich' && <OfferCompare offer={o} />}
        {current.path === 'hoai' && <OfferHoai offer={o} onSaved={refresh} />}
        {current.path === 'dateien' && <OfferFiles offer={o} />}
        </Fragment>
      </Stack>
    </div>
  );
}

/** "Projekt anlegen …" until the offer has its project, then a link to it (AP-98). */
function ProjectButton({ offer }: { offer: Offer }): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (offer.projectId) return <Button size="compact-xs" variant="light" component="a" {...linkTo(navigate, `/projekte/${offer.projectId}/termine`)}>Zum Projekt</Button>;
  return (
    <>
      <Button size="compact-xs" variant={offer.status === 'beauftragt' ? 'filled' : 'light'} onClick={() => setOpen(true)} title="Aus diesem Angebot ein Projekt anlegen – mit Vorschau">Projekt anlegen …</Button>
      {open && <ProjectFromOfferModal offer={offer} onClose={() => setOpen(false)} />}
    </>
  );
}

function StatusMenu({ offer, onDone }: { offer: Offer; onDone: () => Promise<void> }): JSX.Element {
  const set = useMutation({ mutationFn: (status: string) => offersApi.update(offer.id, offer.version, { status }), onSettled: onDone });
  const s = OFFER_STATUS[offer.status as keyof typeof OFFER_STATUS] ?? OFFER_STATUS.entwurf;
  return (
    <Menu position="bottom-start" withinPortal>
      <Menu.Target><Badge component="button" variant="light" color={s.color} style={{ cursor: 'pointer' }} title="Status ändern">{s.label} ▾</Badge></Menu.Target>
      <Menu.Dropdown>
        {Object.entries(OFFER_STATUS).map(([k, v]) => <Menu.Item key={k} onClick={() => set.mutate(k)}>{v.label}</Menu.Item>)}
      </Menu.Dropdown>
    </Menu>
  );
}

/** The variants as tabs, each with its calculation; a new one empty or as a copy. */
function VariantsView({ offer, onChanged }: { offer: Offer; onChanged: () => Promise<void> }): JSX.Element {
  const [chosen, setChosen] = useState<string>(offer.variants[0]!.id);
  const variant = offer.variants.find((v) => v.id === chosen) ?? offer.variants[0]!;
  const nextCode = String.fromCharCode(65 + Math.max(...offer.variants.map((v) => v.code.charCodeAt(0) - 65)) + 1);
  const add = useMutation({
    mutationFn: (copy: boolean) => offersApi.addVariant(offer.id, { code: nextCode, constellation: copy ? variant.constellation : 'bg', copyFrom: copy ? variant.id : null }),
    onSuccess: async ({ id }) => { await onChanged(); setChosen(id); },
  });
  const remove = useMutation({ mutationFn: () => offersApi.removeVariant(offer.id, variant.id), onSuccess: async () => { setChosen(offer.variants[0]!.id); await onChanged(); } });
  return (
    <Stack gap="sm">
      <Group gap="xs" wrap="wrap">
        <Tabs value={variant.id} onChange={(v) => v && setChosen(v)} variant="outline">
          <Tabs.List>
            {offer.variants.map((v) => <Tabs.Tab key={v.id} value={v.id}>{v.code} · {v.name}</Tabs.Tab>)}
          </Tabs.List>
        </Tabs>
        <Menu position="bottom-start" withinPortal>
          <Menu.Target><Button size="xs" variant="light" loading={add.isPending}>+ Variante {nextCode}</Button></Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={() => add.mutate(true)}>als Kopie von {variant.code} (gleiche Positionen, andere Konstellation)</Menu.Item>
            <Menu.Item onClick={() => add.mutate(false)}>leer</Menu.Item>
          </Menu.Dropdown>
        </Menu>
        {offer.variants.length > 1 && (
          <Button size="xs" variant="subtle" color="red" onClick={() => { if (window.confirm(`Variante ${variant.code} löschen?`)) remove.mutate(); }}>Variante {variant.code} löschen</Button>
        )}
      </Group>
      {(add.error ?? remove.error) && <Alert color="red" py={6}>{(add.error ?? remove.error) instanceof ApiError ? (add.error ?? remove.error)!.message : 'Das hat nicht geklappt.'}</Alert>}
      <VariantEditor key={variant.id} offerId={offer.id} variant={variant} onSaved={onChanged} />
    </Stack>
  );
}

/**
 * Edits a variant locally and saves it a moment after the last change, with
 * its version: a change made meanwhile elsewhere is not overwritten.
 */
function VariantEditor({ offerId, variant, onSaved }: { offerId: string; variant: OfferVariant; onSaved: () => Promise<void> }): JSX.Element {
  const [data, setData] = useState<OfferVariantData>(variant.data);
  const [meta, setMeta] = useState({ name: variant.name, constellation: variant.constellation });
  const version = useRef(variant.version);
  const [state, setState] = useState<'saved' | 'pending' | 'saving' | 'error'>('saved');
  const [error, setError] = useState<string | null>(null);
  const dirty = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const save = async (next: OfferVariantData, nextMeta: typeof meta): Promise<void> => {
    setState('saving');
    try {
      const r = await offersApi.saveVariant(offerId, variant.id, version.current, { data: next, name: nextMeta.name, constellation: nextMeta.constellation });
      version.current = r.version;
      dirty.current = false;
      setState('saved');
      setError(null);
      void onSaved();
    } catch (e) {
      setState('error');
      setError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.');
    }
  };
  const latest = useRef({ data, meta });
  const change = (next: OfferVariantData, nextMeta = meta): void => {
    latest.current = { data: next, meta: nextMeta };
    setData(next);
    setMeta(nextMeta);
    dirty.current = true;
    setState('pending');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void save(next, nextMeta), 900);
  };
  // Leaving the page (or the variant) with changes still waiting: save them now.
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (dirty.current) void offersApi.saveVariant(offerId, variant.id, version.current, { data: latest.current.data, name: latest.current.meta.name, constellation: latest.current.meta.constellation });
  }, [offerId, variant.id]);

  return (
    <Stack gap="sm">
      <Group gap="sm" align="flex-end">
        <TextInput size="xs" label="Bezeichnung der Variante" value={meta.name} onChange={(e) => change(data, { ...meta, name: e.currentTarget.value })} w={320} />
        <Select size="xs" label="Konstellation" w={280} data={Object.entries(CONSTELLATION_LABELS).map(([value, label]) => ({ value, label }))} value={meta.constellation}
          onChange={(v) => v && change(data, { ...meta, constellation: v as Constellation })} allowDeselect={false} comboboxProps={{ withinPortal: true }} />
        <Text size="xs" c={state === 'error' ? 'red' : 'dimmed'} pb={6}>
          {state === 'saved' ? 'gespeichert' : state === 'saving' ? 'speichert …' : state === 'pending' ? 'ungespeichert …' : 'nicht gespeichert'}
        </Text>
        {state === 'error' && <Button size="compact-xs" variant="light" onClick={() => void save(data, meta)}>erneut speichern</Button>}
      </Group>
      {error && <Alert color="red" py={6}>{error}</Alert>}
      <OfferCalculation data={data} constellation={meta.constellation} onChange={(next) => change(next)} />
    </Stack>
  );
}
